import { ChangeDetectionStrategy, Component, ElementRef, OnInit, QueryList, Renderer2, ViewChildren } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RESULT_COUNTER } from 'src/app/share/constant/catalog-constant';
import { removeClonedSortingFilter } from 'src/app/share/constant/common-function-constant';
import { RESULT_PER_PAGE } from 'src/app/share/constant/sort-constant';
import { BLANK } from 'src/app/share/constant/special-characters-constant';
import { CATALOG_TAB, DOWNLOAD_TAB, LOADING_REFERENCE, MOBILE_FILTER, SPECIAL_KEYWORD_ALL, TAB_NAV_VARIABLE, TOTAL_TABS, WEBSITE_TAB } from 'src/app/share/constant/variable-constant';
import { SearchFilterParams } from 'src/app/share/model/searchFilter.model';
import { EventBusService } from 'src/app/share/services/event-bus-service/event-bus.service';
import { Util } from 'src/app/share/util/util';
import { SearchContainer } from '../../models/search-container.model';
import { TabNav } from '../../models/tab-nav.model';

@Component({
    selector: 'app-tab-nav',
    templateUrl: './tab-nav.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TabNavComponent implements OnInit {

  @ViewChildren("tabHeadItems") tabHeadItems!: QueryList<ElementRef<HTMLElement>>;

  isGlobalLoading = LOADING_REFERENCE.isGlobalLoading.asObservable();

  // spelling suggestion
  wbsSpell: string = BLANK;
  wbsSuggestionUrl: string = BLANK;
  dwnSpell: string = BLANK;
  dwnSuggestionUrl: string = BLANK;

  countOfAllTabs: number = 0;

  math = Math;
  navs: TabNav[];
  catalogTab!: TabNav;
  downloadTab!: TabNav;
  websiteTab!: TabNav;

  query: string = BLANK;

  result_counter = RESULT_COUNTER;

  specialSearchingKeyword: string = SPECIAL_KEYWORD_ALL;
  currentTab: string = BLANK;
  searchFilterParams!: SearchFilterParams;
  catalogCount = 0;
  websiteCount = 0;
  downloadCount = 0;
  checkEmptyResults: boolean = false;
  isDisplayLocator: boolean = true;

  isProductTabError = false;
  isDownloadsTabError = false;
  isWebsiteTabError = false;

  constructor(private eventBusService: EventBusService,
    private translateService: TranslateService,
    private renderer: Renderer2,
    private util: Util
  ) {
    this.catalogTab = TAB_NAV_VARIABLE.catalogTab(this.translateService.instant('dc.myrn.top.tab.catalog'));
    this.downloadTab = TAB_NAV_VARIABLE.downloadTab(this.translateService.instant('dc.myrn.top.tab.download'));
    this.websiteTab = TAB_NAV_VARIABLE.websiteTab(this.translateService.instant('dc.myrn.top.tab.website'));
    this.navs = [
      this.catalogTab,
      this.downloadTab,
      this.websiteTab,
    ];
  }

  ngOnInit(): void {
    this.sanitizeNumQueryParameter();

    // Get query (search text)
    this.query = this.util.getQuery();

    // Use language
    this.useLanguage();

    // Get result counters for all tabs
    this.getTotalOfResultTabs();
  }

  sanitizeNumQueryParameter() {
    const num = this.util.getParams('num');
    if (!num) return;
    if (!Number.isFinite(+num)) {
      this.util.deleteParam('num');
      return;
    }
    if (parseInt(num) <= 0 || parseInt(num) > RESULT_PER_PAGE[RESULT_PER_PAGE.length - 1].page) {
      this.util.deleteParam('num');
    }
  }

  trackByFn(index: number, item: TabNav): number {
    return item.id;
  }

  private assignCountToTab(tabName: string, countValue: number): void {
    // assign count value to each tab
    this.navs.forEach((item: TabNav) => {
      if (item.title == tabName) {
        item.count = countValue;
      }
    });
  }

  getTotalOfResultTabs(): void {

    const counterPromise = new Promise<void>((resolve) => {
      let counterArray: number[] = [];

      this.eventBusService.on("cTotal", (value, isDisplayLocator, isError) => {
        this.isProductTabError = isError;
        this.catalogCount += value;
        this.isDisplayLocator = isDisplayLocator;
        this.assignCountToTab('dc.myrn.top.tab.catalog', value);
        counterArray.length < (TOTAL_TABS.size - 1) ? counterArray.push(value) : resolve();
      });
      this.eventBusService.on("wSearchContainer", (value, isError) => {
        this.isWebsiteTabError = isError; // unused
        this.websiteCount += value.result.count;
        this.assignCountToTab('dc.myrn.top.tab.website', value.result.count);
        this.assignSpellingSuggestion('wbs', value);
        counterArray.length < (TOTAL_TABS.size - 1) ? counterArray.push(value.result.count) : resolve();
      });
      this.eventBusService.on("searchContainer", (value, isError) => {
        this.isDownloadsTabError = isError; // unused
        this.downloadCount += value.result.count;
        this.assignCountToTab('dc.myrn.top.tab.download', value.result.count);
        this.assignSpellingSuggestion('dwn', value);
        counterArray.length < (TOTAL_TABS.size - 1) ? counterArray.push(value.result.count) : resolve();
      });
    });

    // GA4 event will be emitted twice if the catalog tab has 0 results,
    // so we need to prevent the second dispatch
    let wasGA4Emitted = false;
    counterPromise.then(() => {
      // set active tab following logic
      if (!this.util.getParams('s')) {
        if (this.catalogCount > 0 || this.isProductTabError) {
          return;
        } else if (this.downloadCount > 0) {
          // the following method will emit GA4
          this.setActiveTabByTabName(DOWNLOAD_TAB.selectedTab);
          wasGA4Emitted = true;
          this.util.assignParam('s', DOWNLOAD_TAB.selectedTab);
          return;
        } else if (this.websiteCount > 0) {
          // the following method will emit GA4
          this.setActiveTabByTabName(WEBSITE_TAB.selectedTab);
          wasGA4Emitted = true;
          this.util.assignParam('s', WEBSITE_TAB.selectedTab);
          return;
        }
      }
    }).finally(() => {
      const sParam = this.util.getParams('s');

      if (!wasGA4Emitted) {
        this.eventBusService.emit('triggerViewSearchResultsGA4');
      }

      this.checkEmptyResults = !(this.isProductTabError || this.isDownloadsTabError || this.isWebsiteTabError)
        && ((this.catalogCount + this.websiteCount + this.downloadCount) === 0) && !this.isDisplayLocator;
      this.emitGlobalLoading(LOADING_REFERENCE.hasEmitted);
      LOADING_REFERENCE.firstLoad = false;

      if (this.isProductTabError) {
        this.switchToTab(CATALOG_TAB.selectedTab);
      } else if (this.catalogCount === 0 && (this.isDownloadsTabError || this.isWebsiteTabError)) {
        this.switchToTab(sParam || DOWNLOAD_TAB.selectedTab);
      } else if (sParam === WEBSITE_TAB.selectedTab || sParam === DOWNLOAD_TAB.selectedTab) {
        if (this.isDownloadsTabError || this.isWebsiteTabError) {
          this.switchToTab(sParam || DOWNLOAD_TAB.selectedTab);
        }
      }
    });
  }

  private emitGlobalLoading(hasEmitted: boolean): void {
    if (!hasEmitted) {
      this.eventBusService.emit('isGlobalLoading', hasEmitted);
      LOADING_REFERENCE.hasEmitted = true;
    }
  }

  useLanguage(): void {
    this.translateService.use(this.getLanguage().toLowerCase());
  }

  getLanguage() {
    const languageCode = this.util.getLanguage();
    return (languageCode == 'xc') ? 'en' : languageCode;
  }

  onChangeTabNav(nav: TabNav): void {
    this.setCurrentTab(nav.selectedTab);
  }

  private setCurrentTab(tabName: string): void {
    // assign param 's' to current url
    if (tabName == this.currentTab) return;
    const currentTab = this.util.getParams('s') || CATALOG_TAB.selectedTab;
    const sis_params = this.util.getParams('sis');
    const dnavs_params = this.util.getParams('dnavs');

    this.cacheParams(currentTab, sis_params, dnavs_params);

    this.toggleParams(tabName, sis_params, dnavs_params);

    this.updateNavValue(tabName);
  }

  /**
   * Switch to another tab
   * @param tabName e.g. catalog, download, website
   */
  public switchToTab(tabName: string): void {
    if (tabName === this.currentTab) {
      return;
    }

    this.setCurrentTab(tabName);
    setTimeout(() => this.setActiveTabByTabName(tabName), 100);
  }

  private updateNavValue(tabName: string) {
    if (tabName == this.currentTab) return;
    [CATALOG_TAB, DOWNLOAD_TAB, WEBSITE_TAB].forEach(nav => nav.selected = (nav.selectedTab == tabName));
    this.currentTab = tabName;
    this.util.assignParam('s', tabName);
    this.eventBusService.emit('triggerViewSearchResultsGA4');
    if (!MOBILE_FILTER.isSticky) return;
    removeClonedSortingFilter(this.renderer);
  }

  watchForChanges(tabName: string) {
    this.eventBusService.emit('changeTab', tabName);
    const sParam = this.util.getParams('s');
    if (LOADING_REFERENCE.firstLoad) {
      this.setActiveTabByTabName(sParam);
    } else {
      this.updateNavValue(tabName);
    }
  }

  private cacheParams(currentTab: string, sisParams: string, dnavParams: string): void {
    const searchFilterParamsCopy = { ...this.searchFilterParams };
    switch (currentTab) {
      case DOWNLOAD_TAB.selectedTab:
        searchFilterParamsCopy.dwn_sis = sisParams;
        searchFilterParamsCopy.dwn_dnavs = dnavParams;
        this.searchFilterParams = searchFilterParamsCopy;
        break;

      case WEBSITE_TAB.selectedTab:
        searchFilterParamsCopy.dnavs = dnavParams;
        this.searchFilterParams = searchFilterParamsCopy;
        break;

      default:
        break;
    }
  }

  private toggleParams(tabName: string, sis_params: string, dnavs_params: string): void {
    switch (tabName) {
      case DOWNLOAD_TAB.selectedTab:
        if (this.searchFilterParams) {
          if (sis_params || this.searchFilterParams.dwn_sis) {
            this.util.assignTabParam('sis', tabName, this.searchFilterParams.dwn_sis);
          }
          if (dnavs_params || this.searchFilterParams.dwn_dnavs) {
            this.util.assignTabParam('dnavs', tabName, this.searchFilterParams.dwn_dnavs);
          }
        }
        break;

      case WEBSITE_TAB.selectedTab:
        this.util.assignTabParam('sis', tabName);
        if (this.searchFilterParams && (dnavs_params || this.searchFilterParams.dnavs)) {
          this.util.assignTabParam('dnavs', tabName, this.searchFilterParams.dnavs);
        }
        break;

      case CATALOG_TAB.selectedTab:
        this.util.assignTabParam('sis', tabName);
        this.util.assignTabParam('dnavs', tabName);
        break;

      default:
        break;
    }
  }

  private assignSpellingSuggestion(id: string, data: SearchContainer) {

    if (data.hasOwnProperty("spellingSuggestions")) {
      if (data.spellingSuggestions.length > 0) {
        switch (id) {
          case "wbs": {
            this.wbsSpell = data.spellingSuggestions[0];
            this.wbsSuggestionUrl = this.generateUrlFromSearchParam(this.wbsSpell);
            break;
          }
          case "dwn": {
            this.dwnSpell = data.spellingSuggestions[0];
            this.dwnSuggestionUrl = this.generateUrlFromSearchParam(this.dwnSpell);
          }
        }
      }
    }

  }

  private generateUrlFromSearchParam(spell: string): string {
    const url = new URL(window.location.href);

    url.searchParams.delete('q');
    url.searchParams.append('q', spell);

    return url.href;
  }

  private autoClickFn(id: string): void {
    const findTabHeadElement = this.tabHeadItems.find((tabHead: ElementRef<HTMLElement>) => tabHead.nativeElement.id == id);
    if (!findTabHeadElement) return;
    findTabHeadElement.nativeElement.click();
  }

  private setActiveTabByTabName(tabName: string): void {

    // default tab id is catalog
    switch (tabName) {
      case WEBSITE_TAB.selectedTab:
        this.autoClickFn(WEBSITE_TAB.idClass);
        this.updateNavValue(tabName);
        break;
      case DOWNLOAD_TAB.selectedTab:
        this.autoClickFn(DOWNLOAD_TAB.idClass);
        this.updateNavValue(tabName);
        break;
      case CATALOG_TAB.selectedTab:
        this.autoClickFn(CATALOG_TAB.idClass);
        this.updateNavValue(tabName);
        break;
      default:
        if (CATALOG_TAB.selectedTab == this.currentTab) return;
        [CATALOG_TAB, DOWNLOAD_TAB, WEBSITE_TAB].forEach(nav => nav.selected = (nav.selectedTab == CATALOG_TAB.selectedTab));
        this.currentTab = CATALOG_TAB.selectedTab;
        break;
    }
  }

}
